// ─── The report catalog ──────────────────────────────────────────────────────
//
// One registry for all 17 reports, so the topic menu, the routes and the
// content pane cannot disagree about what exists. A report is registered here
// FIRST and implemented later: `status` says which, and the page renders a
// stated reason rather than a blank pane for everything not yet built.
//
// Two of the 17 are not waiting on implementation but on a data model that does
// not exist yet — `blocked`, with the reason spelled out. Do not "implement"
// those by inventing a proxy metric: 3.3 would need a temperature time series
// (StorageCondition is a declared enum per location, not a reading) and 5.3 a
// deviation record (the only deviation in the schema is free text on
// MaintenanceEvent, equipment-scoped, with no type/severity/root cause).
//
// Ids are the numbering from the spec and slugs are the URL, so both are
// permanent: a bookmarked report must survive the catalog being reordered.

import { PROCEDURE_TYPE_OPTIONS, PROCEDURE_TYPE_LABELS } from '@/types/cgt'
import { STORAGE_NODE_KINDS, STORAGE_NODE_KIND_LABELS } from '@/types/storage'

export type ReportFilterOption = { id: string; label: string }

/**
 * One dimension a report can be sliced by: what the thing IS, never when it
 * happened. The period is the Period column's job and is never repeated here.
 *
 * `options: null` means the values are rows, not an enum (material categories,
 * equipment types), so they arrive with the report's own data. The shell shows
 * the dimension disabled rather than pretending it has nothing to offer.
 */
/**
 * Where a row-backed dimension's options come from.
 *
 * A dimension with `options: null` has no fixed list. Naming the source here
 * keeps the catalog declarative and lets the toolbar fetch it once, before any
 * report has rendered — a filter that cannot be populated until its report
 * arrives cannot shape the first request.
 */
export type ReportFilterOptionsSource = 'materialCategory'

export type ReportFilterDimension = {
  /** URL-safe key; becomes `?f_<id>=a,b` when values are selected. */
  id: string
  label: string
  options: ReportFilterOption[] | null
  /** Where to fetch the options when `options` is null. */
  optionsSource?: ReportFilterOptionsSource
}

const PROCEDURE_TYPE_FILTER: ReportFilterDimension = {
  id: 'type',
  label: 'Procedure type',
  options: PROCEDURE_TYPE_OPTIONS.map((id) => ({ id, label: PROCEDURE_TYPE_LABELS[id] })),
}

// POSITION is excluded: it is the occupiable kind and the schema comments warn
// it is left out of capacity arithmetic, so offering it on 3.1 would invite a
// slice the numbers cannot answer.
const CAPACITY_KIND_FILTER: ReportFilterDimension = {
  id: 'kind',
  label: 'Storage type',
  options: STORAGE_NODE_KINDS.filter((k) => k !== 'POSITION').map((id) => ({
    id,
    label: STORAGE_NODE_KIND_LABELS[id],
  })),
}

const STORAGE_KIND_FILTER: ReportFilterDimension = {
  id: 'kind',
  label: 'Storage type',
  options: STORAGE_NODE_KINDS.map((id) => ({ id, label: STORAGE_NODE_KIND_LABELS[id] })),
}

// The four cycles report 4.2 names. Maintenance event types are a per-tenant
// catalog rather than an enum, so these are the report's own buckets.
const MAINTENANCE_KIND_FILTER: ReportFilterDimension = {
  id: 'maintenance',
  label: 'Maintenance type',
  options: [
    { id: 'calibration', label: 'Calibration' },
    { id: 'preventive', label: 'Preventive' },
    { id: 'qualification', label: 'Qualification' },
    { id: 'inspection', label: 'Inspection' },
  ],
}

const MATERIAL_CATEGORY_FILTER: ReportFilterDimension = {
  id: 'category',
  label: 'Material category',
  // Free text on each material, not an enum, so the values can only come from
  // the tenant's own data — `optionsSource` says where the toolbar fetches them.
  options: null,
  optionsSource: 'materialCategory',
}

const EQUIPMENT_TYPE_FILTER: ReportFilterDimension = {
  id: 'equipmentType',
  label: 'Equipment type',
  options: null,
}

const CAPA_FORM_FILTER: ReportFilterDimension = {
  id: 'form',
  label: 'CAPA form',
  options: null,
}

/** Why a report is not rendering data yet. */
export type ReportStatus =
  /**
   * Not runnable yet.
   *
   * NOT the source of truth for "is this built" — that is membership of the
   * `BUILT` registry in components/reports/builtReports.tsx, which is what
   * actually decides whether a report renders. This field drifted once already
   * (every report in sections 1 and 2 still said `planned` after shipping), so
   * nothing reads it to mean "unavailable"; it survives only to carry the
   * `blocked` case below, which the registry cannot express.
   */
  | { kind: 'planned' }
  /** No data source exists. `followUp` is the ticket that would create one. */
  | { kind: 'blocked'; reason: string; followUp: FollowUpTicket }

/**
 * The ticket a blocked report is waiting on.
 *
 * `id` is null until the follow-up tickets are cut — rendered as plain text
 * rather than a dead link, because a link that goes nowhere is worse than none.
 */
export type FollowUpTicket = { id: string | null; summary: string }

export type ReportDefinition = {
  /** Spec numbering, e.g. '1.1'. Shown in the menu so it matches the ticket. */
  id: string
  /** URL segment. Permanent once shipped. */
  slug: string
  name: string
  description: string
  /**
   * True when the report is a time series and so needs a bucket size on top of
   * the window. Only 1.1, 1.2 and 5.1 are — everything else is a snapshot or a
   * single figure over the whole window, where a grain toggle would be a lie.
   */
  hasGrain: boolean
  /** What this report can be sliced by, beyond the period. */
  filters: ReportFilterDimension[]
  status: ReportStatus
}

export type ReportCategory = {
  /** Spec numbering, e.g. '1'. */
  id: string
  /** URL segment. Permanent once shipped. */
  slug: string
  name: string
  description: string
  reports: ReportDefinition[]
}

export const CLICKUP_TASK_URL = 'https://app.clickup.com/t/'

export const REPORT_CATEGORIES: ReportCategory[] = [
  {
    id: '1',
    slug: 'procedures',
    name: 'Procedure & Service-Level',
    description: 'Volume, throughput, turnaround and backlog across procedure types.',
    reports: [
      {
        id: '1.1',
        slug: 'volume-by-type',
        name: 'Procedure volume by procedure type',
        description: 'Procedures started per bucket, split by Transplant, MES, KIT Creation and Cleaning.',
        hasGrain: true,
        filters: [PROCEDURE_TYPE_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '1.2',
        slug: 'received-vs-completed',
        name: 'Procedures received vs. completed',
        description: 'Intake against output per bucket, showing whether the queue is growing or draining.',
        hasGrain: true,
        filters: [PROCEDURE_TYPE_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '1.3',
        slug: 'on-time-completion',
        name: 'On-time procedure completion',
        description:
          'Share completed on or before their due date. No due date set is reported separately, never folded into the percentage.',
        hasGrain: false,
        filters: [PROCEDURE_TYPE_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '1.4',
        slug: 'turnaround-time',
        name: 'Procedure turnaround time by procedure type',
        description:
          'Time from start to completion per procedure type: average, median and spread.',
        hasGrain: false,
        filters: [PROCEDURE_TYPE_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '1.5',
        slug: 'backlog-aging',
        name: 'Open procedure backlog and aging',
        description: 'Procedures still in progress, bucketed by how long they have been open.',
        hasGrain: false,
        filters: [PROCEDURE_TYPE_FILTER],
        status: { kind: 'planned' },
      },
    ],
  },
  {
    id: '2',
    slug: 'inventory',
    name: 'Inventory',
    description: 'Stock levels, consumption and expiry risk across materials.',
    reports: [
      {
        id: '2.1',
        slug: 'below-minimum',
        name: 'Items at zero or below minimum level',
        description: 'Materials out of stock or under their minimum, grouped by category.',
        hasGrain: false,
        filters: [MATERIAL_CATEGORY_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '2.2',
        slug: 'consumption-by-procedure',
        name: 'Inventory consumption by procedure',
        description: 'What each procedure consumes, and which procedures require a given item.',
        hasGrain: false,
        filters: [PROCEDURE_TYPE_FILTER, MATERIAL_CATEGORY_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '2.3',
        slug: 'expiry-risk',
        name: 'Expiration and expiry-risk',
        description: 'Lots already expired and those expiring inside the window, by material and quantity.',
        hasGrain: false,
        filters: [MATERIAL_CATEGORY_FILTER],
        status: { kind: 'planned' },
      },
    ],
  },
  {
    id: '3',
    slug: 'storage',
    name: 'Storage',
    description: 'Capacity, utilisation and conditions across the storage hierarchy.',
    reports: [
      {
        id: '3.1',
        slug: 'capacity-utilisation',
        name: 'Capacity and utilisation',
        description: 'Occupied against derived capacity, by storage type.',
        hasGrain: false,
        filters: [CAPACITY_KIND_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '3.2',
        slug: 'weekly-location-usage',
        name: 'Weekly storage-location usage',
        description: 'Locations used per week, by storage type.',
        hasGrain: false,
        filters: [STORAGE_KIND_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '3.3',
        slug: 'temperature-excursions',
        name: 'Temperature excursions and affected products',
        description:
          'Excursions against the validated range for a location, and the products held there at the time.',
        hasGrain: false,
        filters: [],
        status: {
          kind: 'blocked',
          // The long version, kept out of the UI: StorageCondition is a
          // declared, validated condition per location — an enum — and the
          // legacy StorageZone carries only a free-text tempRange. Nothing
          // records a sensor reading, so nothing can record an excursion.
          reason: 'Nothing in the schema records a temperature reading, so there is no excursion to report.',
          followUp: {
            id: null,
            summary: 'Add a temperature reading / excursion model for storage locations',
          },
        },
      },
    ],
  },
  {
    id: '4',
    slug: 'equipment',
    name: 'Equipment & Maintenance',
    description: 'Utilisation, upcoming maintenance and reliability across the equipment fleet.',
    reports: [
      {
        id: '4.1',
        slug: 'utilisation',
        name: 'Equipment utilisation',
        description: 'Reserved hours against the 168-hour week, per unit and equipment type.',
        hasGrain: false,
        filters: [EQUIPMENT_TYPE_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '4.2',
        slug: 'upcoming-maintenance',
        name: 'Upcoming maintenance',
        description: 'Calibration, preventive, qualification and inspection events falling due.',
        hasGrain: false,
        filters: [MAINTENANCE_KIND_FILTER, EQUIPMENT_TYPE_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '4.3',
        slug: 'downtime-reliability',
        name: 'Downtime, availability and reliability',
        description: 'Time out of service, availability rate and time between failures, per unit.',
        hasGrain: false,
        filters: [EQUIPMENT_TYPE_FILTER],
        status: { kind: 'planned' },
      },
    ],
  },
  {
    id: '5',
    slug: 'quality',
    name: 'Quality & CAPA',
    description: 'CAPA throughput and closure performance.',
    reports: [
      {
        id: '5.1',
        slug: 'created-vs-closed',
        name: 'CAPAs created vs. closed',
        description: 'CAPAs opened against CAPAs closed per bucket.',
        hasGrain: true,
        filters: [PROCEDURE_TYPE_FILTER, CAPA_FORM_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '5.2',
        slug: 'closure-time',
        name: 'Average and median CAPA closure time',
        description: 'Time from open to close, average and median, over the window.',
        hasGrain: false,
        filters: [PROCEDURE_TYPE_FILTER, CAPA_FORM_FILTER],
        status: { kind: 'planned' },
      },
      {
        id: '5.3',
        slug: 'deviations',
        name: 'Deviations by type, severity, root cause and recurrence',
        description: 'Deviation counts broken down by classification, and repeat occurrences.',
        hasGrain: false,
        filters: [],
        status: {
          kind: 'blocked',
          // The long version: the only deviation in the schema is a free-text
          // String on MaintenanceEvent plus a deviation_recorded audit action,
          // both equipment-scoped, neither carrying a classification.
          reason:
            'The only deviation in the schema is free text on a maintenance event, with no type, severity or root cause to group by.',
          followUp: {
            id: null,
            summary: 'Add a deviation model with type, severity and root cause',
          },
        },
      },
    ],
  },
]

/** Every report, flattened — for counts and lookups that don't care about topic. */
export const ALL_REPORTS: ReportDefinition[] = REPORT_CATEGORIES.flatMap((c) => c.reports)

export function findCategory(slug: string | undefined): ReportCategory | undefined {
  if (!slug) return undefined
  return REPORT_CATEGORIES.find((c) => c.slug === slug)
}

export function findReport(
  categorySlug: string | undefined,
  reportSlug: string | undefined,
): { category: ReportCategory; report: ReportDefinition } | undefined {
  const category = findCategory(categorySlug)
  if (!category || !reportSlug) return undefined
  const report = category.reports.find((r) => r.slug === reportSlug)
  return report ? { category, report } : undefined
}

/** Path to a report, without the time frame — callers merge that in as a query. */
export function reportPath(category: ReportCategory, report: ReportDefinition): string {
  return `/reports/${category.slug}/${report.slug}`
}

/** A follow-up ticket's URL, or null while the ticket has not been cut yet. */
export function followUpUrl(ticket: FollowUpTicket): string | null {
  return ticket.id ? `${CLICKUP_TASK_URL}${ticket.id}` : null
}
